import { Maybe, Offer, Receipt } from '@gql/schema';
import { TravellersSelection, defaultSelection } from '@secretescapes/cauldron';
import { groupBy, isEqual } from '@utils/lodash.utils';

export const ROOM_URGENCY_LIMIT = 5;

export const unavailableForDates = (showFinalRates: boolean, receipt: Maybe<Receipt>) => {
  return showFinalRates && !receipt;
};

export const unsuitableForTravellers = (
  travellersSelection: Maybe<TravellersSelection>,
  noAvailabilityReturned: boolean
) => {
  const isTravellersSelectionChanged =
    !!travellersSelection && !isEqual(travellersSelection, defaultSelection);

  return isTravellersSelectionChanged && noAvailabilityReturned;
};

export const notEnoughRooms = (numberOfRooms: number, offer: Offer) => {
  return numberOfRooms > (offer.prices?.maxAvailability ?? 0);
};

export const isBookable = (showFinalRates: boolean, receipt: Maybe<Receipt>) => {
  return showFinalRates && !!receipt;
};

export const lowRooms = (availableRoomCount: Maybe<number>) => {
  return availableRoomCount && availableRoomCount < ROOM_URGENCY_LIMIT;
};

export enum InfoBoxType {
  UNAVAILABLE_FOR_DATES = 'UNAVAILABLE_FOR_DATES',
  NOT_ENOUGH_ROOMS = 'NOT_ENOUGH_ROOMS',
  LOW_ROOMS = 'LOW_ROOMS',
  UNSUITABLE_ROOM_FOR_TRAVELLERS = 'UNSUITABLE_ROOM_FOR_TRAVELLERS',
}

export function getInfoBoxType(
  showFinalRates: boolean,
  numberOfRooms: number,
  offer: Offer,
  noAvailabilityReturned: boolean,
  travellersSelection?: TravellersSelection
): InfoBoxType | null {
  if (notEnoughRooms(numberOfRooms, offer)) {
    return InfoBoxType.NOT_ENOUGH_ROOMS;
  }

  // TODO - Replace with !offer.pricing check when using facets and api bugfix is released
  if (unsuitableForTravellers(travellersSelection, noAvailabilityReturned)) {
    return InfoBoxType.UNSUITABLE_ROOM_FOR_TRAVELLERS;
  }

  if (unavailableForDates(showFinalRates, offer.receipt)) {
    return InfoBoxType.UNAVAILABLE_FOR_DATES;
  }

  if (isBookable(showFinalRates, offer.receipt) && lowRooms(offer.receipt?.maxAvailability)) {
    return InfoBoxType.LOW_ROOMS;
  }

  return null;
}

export function disableOffer(infoBoxType: InfoBoxType | null) {
  return (
    !!infoBoxType &&
    [
      InfoBoxType.UNAVAILABLE_FOR_DATES,
      InfoBoxType.UNSUITABLE_ROOM_FOR_TRAVELLERS,
      InfoBoxType.NOT_ENOUGH_ROOMS,
    ].includes(infoBoxType)
  );
}

export const sortOffers = (offers: Offer[], getInfoBox: (o: Offer) => InfoBoxType | null) => {
  // This sorts offers in the following order:
  // Available Rooms -> Unavailable on dates selected -> Not enough rooms (rooms selected > maxAvailability)

  const groupedOffers = groupBy(offers, o => {
    const offerInfoBoxType = getInfoBox(o);

    switch (offerInfoBoxType) {
      case InfoBoxType.UNSUITABLE_ROOM_FOR_TRAVELLERS:
        return 1;
      case InfoBoxType.UNAVAILABLE_FOR_DATES:
        return 2;
      case InfoBoxType.NOT_ENOUGH_ROOMS:
        return 3;
      default:
        return 0;
    }
  });

  return Object.values(groupedOffers).flat();
};
